import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from "lucide-react";
import type { ChangeEvent, ReactNode } from "react";
import { useEffect, useId, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";
import type { CompanyDetail, CompanyInput, UploadFileSchema } from "@upclaim/sdk/src/data-contracts";

import { Can } from "@/components/can";
import { fileToPayload } from "@/components/files/use-file-to-payload";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Command,
  CommandCollection,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { openBoxFile } from "@/features/claims/lib/open-box";
import { cn } from "@/lib/utils";

import { useCompanyDetail, useCourts, useUploadCompanyKbis, useUpsertCompany } from "../api/use-companies";

type AddressInput = NonNullable<CompanyInput["postalAddress"]>;

/** Vue useCorrectValue — prefer the edited value, fall back to the loaded one, coerce to null. */
function useCorrectValue<T>(primary: T | null | undefined, secondary: T | null | undefined): T | null {
  return (primary !== null && primary !== undefined ? primary : secondary) || null;
}

function toAddressInput(address: CompanyDetail["postalAddress"]): AddressInput | undefined {
  if (!address) {
    return undefined;
  }
  return {
    label: address.label,
    nameLine: address.nameLine,
    streetAddress: address.streetAddress,
    streetAddressComplement: address.streetAddressComplement,
    city: address.city,
    zipCode: address.zipCode,
    region: address.region,
    countryCode: address.countryCode,
  };
}

interface CompanyLegalSheetProps {
  open: boolean;
  companyId?: string | null;
  /** Preloaded (partial) company — the detail is lazy-fetched when the sheet shows. */
  company?: CompanyDetail | null;
  relation?: { travelAgencyId: string } | { travelAgencyCompanyId: string } | { airlineId: string };
  onSaved: () => void;
  /** Rendered next to the save button (e.g. the dialog's close action). */
  footerSlot?: ReactNode;
}

/** Port of CompanyModal.vue — legal-entity sheet with collapsible sections. */
export function CompanyLegalSheet({
  open,
  companyId,
  company,
  relation,
  onSaved,
  footerSlot,
}: CompanyLegalSheetProps) {
  const detailQuery = useCompanyDetail(companyId, open);
  const upsertCompany = useUpsertCompany();
  const uploadKbis = useUploadCompanyKbis();

  const companyDetail = detailQuery.data ?? company ?? null;
  const isLoading = detailQuery.isLoading;

  const [input, setInput] = useState<CompanyInput>({});
  const [kbisInput, setKbisInput] = useState<UploadFileSchema | null>(null);

  // Prefill the editable state whenever the (lazy-loaded) detail arrives.
  useEffect(() => {
    setInput({
      name: companyDetail?.name,
      siren: companyDetail?.siren,
      siret: companyDetail?.siret,
      euVAT: companyDetail?.euVAT,
      lreEmailAddress: companyDetail?.lreEmailAddress,
      legalContactEmail: companyDetail?.legalContactEmail,
      domain: companyDetail?.domain,
      logoUrl: companyDetail?.logoUrl,
      competentCourtId: companyDetail?.competentCourtId,
      legalNotices: companyDetail?.legalNotices,
      postalAddress: toAddressInput(companyDetail?.postalAddress),
      headquartersPostalAddress: toAddressInput(companyDetail?.headquartersPostalAddress),
    });
  }, [companyDetail]);

  function patch(partial: Partial<CompanyInput>) {
    setInput((current) => ({ ...current, ...partial }));
  }

  function patchAddress(key: "postalAddress" | "headquartersPostalAddress", partial: Partial<AddressInput>) {
    setInput((current) => ({
      ...current,
      [key]: { streetAddress: null, city: null, ...current[key], ...partial },
    }));
  }

  async function onKbisFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }
    const { filename, dataUrl, mimeType } = await fileToPayload(file);
    setKbisInput({ name: filename, document: dataUrl, mimeType });
  }

  async function submit() {
    try {
      const saved = await upsertCompany.mutateAsync({
        id: companyId,
        name: useCorrectValue(input.name, companyDetail?.name),
        siren: useCorrectValue(input.siren, companyDetail?.siren),
        siret: useCorrectValue(input.siret, companyDetail?.siret),
        euVAT: useCorrectValue(input.euVAT, companyDetail?.euVAT),
        lreEmailAddress: useCorrectValue(input.lreEmailAddress, companyDetail?.lreEmailAddress),
        legalContactEmail: useCorrectValue(input.legalContactEmail, companyDetail?.legalContactEmail),
        domain: useCorrectValue(input.domain, companyDetail?.domain),
        logoUrl: useCorrectValue(input.logoUrl, companyDetail?.logoUrl),
        competentCourtId: useCorrectValue(input.competentCourtId, companyDetail?.competentCourtId),
        legalNotices: useCorrectValue(input.legalNotices, companyDetail?.legalNotices),
        postalAddress: input.postalAddress,
        headquartersPostalAddress: input.headquartersPostalAddress,
        ...(relation ?? {}),
      });

      if (kbisInput && saved.id) {
        await uploadKbis.mutateAsync({ companyId: saved.id, file: kbisInput });
        setKbisInput(null);
      }
      onSaved();
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  const submitting = upsertCompany.isPending || uploadKbis.isPending;

  return (
    <div className="space-y-6">
      <Section title="Informations Juridiques">
        <div className="space-y-2">
          <Field label="Nom de la société" value={input.name} onChange={(name) => patch({ name })} />
          <div className="grid grid-cols-2 gap-x-2">
            <Field
              label="SIREN"
              placeholder="Exemple : 890120587"
              value={input.siren}
              onChange={(siren) => patch({ siren })}
            />
            <Field
              label="SIRET"
              placeholder="Exemple : 89012058700021"
              value={input.siret}
              onChange={(siret) => patch({ siret })}
            />
          </div>
          <Field
            label="Numéro TVA Européenne"
            description="Si société étrangère"
            placeholder="Exemple : ATU72316013"
            value={input.euVAT}
            onChange={(euVAT) => patch({ euVAT })}
          />
          {!isLoading ? (
            <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
              <CourtSelect
                value={input.competentCourtId ?? null}
                onChange={(competentCourtId) => patch({ competentCourtId })}
              />
            </Can>
          ) : null}
        </div>
      </Section>

      <Section title="Adresse Postale">
        <AddressFields
          address={input.postalAddress}
          onChange={(partial) => patchAddress("postalAddress", partial)}
        />
      </Section>

      <Section title="Adresse Postale du Siège de la compagnie">
        <AddressFields
          address={input.headquartersPostalAddress}
          onChange={(partial) => patchAddress("headquartersPostalAddress", partial)}
        />
      </Section>

      <Section title="Mentions Légales">
        <Label className="block text-sm font-medium text-gray-900">Mentions légales de la société :</Label>
        <Textarea
          rows={4}
          className="mt-2"
          value={input.legalNotices ?? ""}
          onChange={(event) => patch({ legalNotices: event.currentTarget.value })}
        />
      </Section>

      <Section title="Adresse email LRE">
        <Field
          label="Adresse email pour envoyer les Lettres Recommandées Electronique"
          value={input.lreEmailAddress}
          onChange={(lreEmailAddress) => patch({ lreEmailAddress })}
        />
      </Section>

      <Section title="Email contact juridique (compagnie)">
        <Field
          label="Adresse email du contact juridique de la compagnie"
          value={input.legalContactEmail}
          onChange={(legalContactEmail) => patch({ legalContactEmail })}
        />
      </Section>

      <Section title="Logo de l'entreprise" defaultOpen={false}>
        {companyDetail?.logoUrl ? (
          <span className="mb-2 inline-block size-14 overflow-hidden rounded-full bg-gray-100">
            <img className="size-10 rounded-md" src={companyDetail.logoUrl} alt="" />
          </span>
        ) : null}
        <Field label="Adresse du Logo" value={input.logoUrl} onChange={(logoUrl) => patch({ logoUrl })} />
      </Section>

      <Section title="KBIS">
        {companyDetail?.kbisBoxFileId ? (
          <div className="rounded-md bg-green-50 p-4">
            <p className="text-sm font-medium text-green-800">Un KBIS est enregistré pour cette société</p>
            <div className="mt-4">
              <Button
                variant="outline"
                disabled={submitting}
                onClick={() => openBoxFile(companyDetail.kbisBoxFileId as string)}
              >
                <ExternalLinkIcon data-icon="inline-start" />
                Voir sur Box
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="px-2 italic">Aucun KBIS renseigné pour l'instant</p>
            <Input type="file" className="mt-2" onChange={(event) => void onKbisFileSelected(event)} />
            {kbisInput?.name ? (
              <p className="mt-1 text-xs text-gray-500">Fichier sélectionné : {kbisInput.name}</p>
            ) : null}
          </div>
        )}
      </Section>

      <div className="flex justify-end gap-2">
        {footerSlot}
        <Button disabled={submitting || isLoading} onClick={() => void submit()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [sectionOpen, setSectionOpen] = useState(defaultOpen);

  return (
    <Collapsible open={sectionOpen} onOpenChange={setSectionOpen}>
      <CollapsibleTrigger className="flex w-full justify-between rounded-lg bg-purple-100 px-4 py-2 text-left text-sm font-medium text-purple-900 hover:bg-purple-200">
        <span>{title}</span>
        <ChevronUpIcon
          className={cn("h-5 w-5 text-purple-500 transition-transform", sectionOpen && "rotate-180")}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-2 pt-4 text-sm text-gray-500">{children}</CollapsibleContent>
    </Collapsible>
  );
}

interface FieldProps {
  label: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  description?: string;
}

function Field({ label, value, onChange, placeholder, description }: FieldProps) {
  const id = useId();

  return (
    <div>
      <Label htmlFor={id} className="block text-sm font-medium text-gray-900">
        {label}
      </Label>
      {description ? <span className="block text-xs italic text-slate-600">({description})</span> : null}
      <Input
        id={id}
        className="mt-2"
        value={value ?? ""}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function AddressFields({
  address,
  onChange,
}: {
  address: AddressInput | undefined;
  onChange: (partial: Partial<AddressInput>) => void;
}) {
  return (
    <div className="space-y-2">
      <Field
        label="Description de l'adresse"
        placeholder="Exemple : Siège de la société"
        value={address?.label}
        onChange={(label) => onChange({ label })}
      />
      <Field
        label="Destinataire"
        placeholder="Exemple : Air France"
        value={address?.nameLine}
        onChange={(nameLine) => onChange({ nameLine })}
      />
      <Field
        label="Adresse"
        placeholder="Exemple : 15 rue Simone Veil"
        value={address?.streetAddress}
        onChange={(streetAddress) => onChange({ streetAddress })}
      />
      <Field
        label="Complément adresse"
        placeholder="Exemple : Immeuble Atlantis"
        value={address?.streetAddressComplement}
        onChange={(streetAddressComplement) => onChange({ streetAddressComplement })}
      />
      <div className="grid grid-cols-3 gap-x-2">
        <Field
          label="Code postal"
          placeholder="Ex : 94000"
          value={address?.zipCode}
          onChange={(zipCode) => onChange({ zipCode })}
        />
        <Field
          label="Ville"
          placeholder="Ex : Paris"
          value={address?.city}
          onChange={(city) => onChange({ city })}
        />
        <Field
          label="Pays"
          placeholder="FR"
          description="Code ISO à 2 lettres"
          value={address?.countryCode}
          onChange={(countryCode) => onChange({ countryCode })}
        />
      </div>
    </div>
  );
}

type CourtOption = { id: number | null; name: string };

function CourtSelect({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (courtId: number | null) => void;
}) {
  const { data: courts = [] } = useCourts(true);
  const [open, setOpen] = useState(false);

  const options: CourtOption[] = [
    { id: null, name: "Aucun" },
    ...courts.map((court) => ({ id: court.id, name: court.name })),
  ];
  const selected = courts.find((court) => court.id === value) ?? null;

  return (
    <div>
      <Label className="block text-sm font-medium text-gray-900">Tribunal compétent</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={cn(
            "mt-2 flex w-full items-center justify-between gap-x-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm",
            selected ? "text-gray-900" : "text-gray-400",
          )}
        >
          <span className="truncate">{selected?.name ?? "Aucun"}</span>
          <ChevronDownIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true" />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--anchor-width) min-w-56 p-0">
          <Command items={options} itemToStringValue={(court) => (court as CourtOption).name}>
            <CommandInput placeholder="Rechercher un tribunal" />
            <CommandEmpty>Pas de résultat.</CommandEmpty>
            <CommandList>
              <CommandCollection>
                {(court: CourtOption) => (
                  <CommandItem
                    key={court.id ?? "none"}
                    value={court}
                    onClick={() => {
                      onChange(court.id);
                      setOpen(false);
                    }}
                  >
                    {court.name}
                  </CommandItem>
                )}
              </CommandCollection>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
